import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { AppShell } from "../components/AppShell"
import { useApi } from "../api/hooks"
import {
  deleteReceipt,
  listReceipts,
  processReceipt,
  requestUploadUrl,
  uploadToS3,
  type ReceiptRow,
} from "../api/receipts"
import { formatBytes, formatEuro } from "../lib/format"
import { useT } from "../lib/i18n"

type UploadState =
  | { kind: "idle" }
  | { kind: "uploading"; step: "presign" | "s3" | "process"; file: File }
  | { kind: "error"; message: string }

const ALLOWED = ["image/jpeg", "image/png", "image/heic", "image/heif", "application/pdf"]
const MAX_BYTES = 6 * 1024 * 1024

export function ReceiptsPage() {
  const api = useApi()
  const nav = useNavigate()
  const { t } = useT()
  const inputRef = useRef<HTMLInputElement>(null)
  const [items, setItems] = useState<ReceiptRow[] | null>(null)
  const [upload, setUpload] = useState<UploadState>({ kind: "idle" })

  const refresh = () =>
    listReceipts(api).then((r) => setItems(r.items)).catch((e: unknown) =>
      setUpload({ kind: "error", message: e instanceof Error ? e.message : String(e) })
    )

  useEffect(() => {
    void refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api])

  async function onPickFile(file: File) {
    if (!ALLOWED.includes(file.type)) {
      setUpload({ kind: "error", message: `${t("unsupported_type")}: ${file.type}` })
      return
    }
    if (file.size > MAX_BYTES) {
      setUpload({ kind: "error", message: `${t("too_large")}: ${formatBytes(file.size)} (max ${formatBytes(MAX_BYTES)})` })
      return
    }
    try {
      setUpload({ kind: "uploading", step: "presign", file })
      const presign = await requestUploadUrl(api, file.name, file.type, file.size)
      setUpload({ kind: "uploading", step: "s3", file })
      await uploadToS3(presign.put_url, file, file.type)
      setUpload({ kind: "uploading", step: "process", file })
      await processReceipt(api, presign.receipt_id)
      setUpload({ kind: "idle" })
      nav(`/receipts/${presign.receipt_id}`)
    } catch (e: unknown) {
      setUpload({ kind: "error", message: e instanceof Error ? e.message : String(e) })
    }
  }

  async function onDelete(receiptId: string) {
    if (!confirm(t("confirm_delete_receipt"))) return
    try {
      await deleteReceipt(api, receiptId)
      await refresh()
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : String(e))
    }
  }

  return (
    <AppShell>
      <h1 style={{ margin: 0, fontSize: 28 }}>{t("receipts_title")}</h1>

      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault()
          const f = e.dataTransfer.files?.[0]
          if (f) void onPickFile(f)
        }}
        onClick={() => inputRef.current?.click()}
        style={{
          marginTop: 24, padding: 32, borderRadius: "var(--radius)",
          border: "2px dashed var(--border)", background: "var(--surface)",
          textAlign: "center", cursor: "pointer",
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ALLOWED.join(",")}
          capture="environment"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0]
            if (f) void onPickFile(f)
            e.target.value = ""
          }}
        />
        {upload.kind === "uploading" && (
          <p style={{ margin: 0, color: "var(--muted)" }}>
            {labelFor(upload.step, t)} <code>{upload.file.name}</code>…
          </p>
        )}
        {upload.kind === "idle" && (
          <p style={{ margin: 0, color: "var(--muted)" }}>
            {t("drag_or_tap")}
          </p>
        )}
        {upload.kind === "error" && (
          <p style={{ margin: 0, color: "#ff7a85" }}>{upload.message}</p>
        )}
      </div>

      <h2 style={{ marginTop: 40, fontSize: 16, color: "var(--muted)" }}>{t("history")}</h2>
      {items === null && <p style={{ color: "var(--muted)" }}>{t("loading")}</p>}
      {items && items.length === 0 && <p style={{ color: "var(--muted)" }}>{t("none_yet")}</p>}
      {items && items.length > 0 && (
        <table style={{ width: "100%", marginTop: 8, borderCollapse: "collapse", fontSize: 14 }}>
          <thead style={{ color: "var(--muted)", textAlign: "left" }}>
            <tr>
              <th style={th}>{t("th_date")}</th>
              <th style={th}>{t("th_merchant")}</th>
              <th style={{ ...th, textAlign: "right" }}>{t("th_total")}</th>
              <th style={th}>{t("th_status")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => {
              const receiptId = r.SK.split("#").at(-1) ?? ""
              return (
                <tr key={r.SK} style={{ borderTop: "1px solid var(--border)" }}>
                  <td style={td}>{r.purchase_date ?? "—"}</td>
                  <td style={td}>
                    <a href={`/receipts/${receiptId}`}
                       onClick={(e) => { e.preventDefault(); nav(`/receipts/${receiptId}`) }}
                       style={{ color: "var(--accent)" }}>
                      {r.merchant ?? "(no merchant)"}
                    </a>
                  </td>
                  <td style={{ ...td, textAlign: "right" }}>
                    {r.total_eur != null ? formatEuro(r.total_eur) : "—"}
                  </td>
                  <td style={td}>{r.status}</td>
                  <td style={{ ...td, textAlign: "right" }}>
                    <button onClick={() => void onDelete(receiptId)} style={delBtnStyle}>{t("delete")}</button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </AppShell>
  )
}

function labelFor(step: "presign" | "s3" | "process", t: (key: import("../lib/i18n").StringKey) => string): string {
  if (step === "presign") return t("uploading")
  if (step === "s3") return t("uploading")
  return t("extracting")
}

const th: React.CSSProperties = { padding: "8px 4px", fontWeight: 500 }
const td: React.CSSProperties = { padding: "10px 4px" }
const delBtnStyle: React.CSSProperties = {
  background: "transparent", color: "#ff7a85",
  border: "1px solid var(--border)", borderRadius: 6, padding: "4px 8px",
  cursor: "pointer", fontSize: 12,
}
